import { useState } from 'react';
import type { AvailableTransition } from '@tasks/contracts';

import { Modal } from '../../components/Modal';
import { Button } from '../../components/Button';
import { Select, type SelectOption } from '../../components/Select';
import { resolutionLabels } from '../../i18n/enums';
import type { Resolution } from '../../lib/types';
import styles from './TransitionDialog.module.css';

const RESOLUTION_OPTIONS: readonly SelectOption<Resolution>[] = (
  Object.keys(resolutionLabels) as Resolution[]
).map((value) => ({ value, label: resolutionLabels[value] }));

export type TransitionDialogProps = {
  readonly open: boolean;
  readonly transition: AvailableTransition | null;
  readonly onClose: () => void;
  readonly onConfirm: (resolution: Resolution | null) => void;
  readonly isPending: boolean;
  readonly error?: string | null;
};

/** UC-ISS-04 AC3: при `requireResolution` форма требует выбрать резолюцию. */
export function TransitionDialog({
  open,
  transition,
  onClose,
  onConfirm,
  isPending,
  error,
}: TransitionDialogProps) {
  const [resolution, setResolution] = useState<Resolution | null>(null);
  const isResolutionMissing = (transition?.requireResolution ?? false) && resolution === null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Переход: ${transition?.name ?? ''}`}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isPending}>
            Отмена
          </Button>
          <Button
            variant="primary"
            loading={isPending}
            disabled={isResolutionMissing}
            onClick={() => onConfirm(resolution)}
          >
            Подтвердить
          </Button>
        </>
      }
    >
      <div className={styles['body']}>
        <p className={styles['target']}>
          Новый статус: <strong>{transition?.toStatus.name}</strong>
        </p>
        {transition?.requireResolution ? (
          <Select
            label="Резолюция"
            options={RESOLUTION_OPTIONS}
            value={resolution}
            placeholder="Выберите резолюцию"
            onChange={(value) => setResolution(value)}
          />
        ) : null}
        {error ? (
          <p className={styles['error']} role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
